import {Component, type ErrorInfo, type ReactNode} from 'react';
import {useI18n} from '../i18n';

/**
 * The last line before a white screen.
 *
 * A guest who gets a blank page puts the phone down and waves at a server, and
 * the venue loses the labour saving the whole feature exists to produce. So a
 * render that throws produces a sentence and a way out, in the guest's own
 * language, and never a stack trace: `error.message` from the API is unstable
 * English for operators, and a JavaScript error message is worse than that.
 */

interface State {
  failed: boolean;
}

export class ErrorBoundary extends Component<{children: ReactNode}, State> {
  state: State = {failed: false};

  static getDerivedStateFromError(): State {
    return {failed: true};
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is the only sink this surface has. There is no error reporting
    // endpoint in the contract, and adding a third-party one to a page a guest
    // loads on 4G would cost the perf budget and raise a PDPA question nobody
    // has answered.
    console.error('pokpok-web render failure', error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return <RenderFailure />;
  }
}

function RenderFailure() {
  const {t} = useI18n();
  return (
    <main
      style={{
        padding: 'var(--pokpok-space-6)',
        display: 'grid',
        gap: 'var(--pokpok-space-4)',
        minHeight: '60dvh',
        alignContent: 'center',
      }}>
      <p>{t('error.generic')}</p>
      <p style={{color: 'var(--color-text-secondary)'}}>{t('common.askStaff')}</p>
      <button
        className="pokpok-primary-action"
        type="button"
        onClick={() => globalThis.location.reload()}
        style={{
          background: 'var(--color-accent)',
          color: 'var(--color-on-accent)',
          borderRadius: 'var(--pokpok-radius-2)',
          padding: '0 var(--pokpok-space-6)',
        }}>
        {t('common.retry')}
      </button>
    </main>
  );
}
